import {
  anchorHit,
  CONFIG,
  deriveResult,
  getMarket,
  gradeLeg,
  legMultiplier,
  scorerIdsFromEvents,
  type Fixture,
  type FixtureResult,
  type Prediction
} from '@h2h/shared';
import { formatMultiplier } from '../../lib/format';
import { legValue, outcomeWord } from './helpers';
import type { CardRow, MarketState } from './types';

/** Build card rows from my prediction vs the (running/final) result. */
export function buildCardRows(
  myPick: Prediction,
  fixture: Fixture,
  final: boolean,
  result: FixtureResult | null | undefined,
  live: boolean,
  liveResult: FixtureResult | null
): CardRow[] {
  const gRes: FixtureResult | null = final && result ? result : live && liveResult ? liveResult : null;
  const out: CardRow[] = [];

  const baseHit = gRes ? anchorHit(myPick, gRes) : false;
  out.push({
    label: 'Match result',
    value: outcomeWord(myPick.outcome, fixture.homeTeam, fixture.awayTeam),
    state: gRes ? (final ? (baseHit ? 'hit' : 'miss') : baseHit ? 'ontrack' : 'offtrack') : 'open',
    factor: `+${CONFIG.COMBO.BASE_POINTS}`
  });

  for (const leg of myPick.legs ?? []) {
    const m = getMarket(leg.marketId);
    if (!m) continue;
    const g = gRes ? gradeLeg(leg, gRes) : { gradeable: false, hit: false };
    const state: MarketState = !g.gradeable
      ? 'open'
      : final
        ? g.hit
          ? 'hit'
          : 'miss'
        : g.hit
          ? 'ontrack'
          : 'offtrack';
    const missed = state === 'miss' || state === 'offtrack';
    out.push({
      label: m.label,
      value: legValue(leg, m, fixture),
      state,
      factor: `${missed ? '÷' : '×'}${formatMultiplier(legMultiplier(leg.marketId, leg.selection))}`
    });
  }
  return out;
}

export function buildLiveResult(fixture: Fixture, live: boolean): FixtureResult | null {
  if (!live) return null;
  // While live we always grade against the running scoreline — defaulting to the
  // current 0–0 just after kickoff — so the "if it ends now" card reads as alive
  // from the first whistle rather than sitting blank until the first goal lands.
  const liveH = fixture.liveHomeGoals ?? 0;
  const liveA = fixture.liveAwayGoals ?? 0;
  const liveHt =
    fixture.liveHalftimeHome != null && fixture.liveHalftimeAway != null
      ? { home: fixture.liveHalftimeHome, away: fixture.liveHalftimeAway }
      : null;
  return deriveResult(fixture.fixtureId, liveH, liveA, scorerIdsFromEvents(fixture.events), liveHt);
}
